import { useEffect, useRef } from "react";

export interface UseInfiniteScrollLoadMoreOptions {
  scrollElement: HTMLElement | null;
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  thresholdPx?: number;
  /** Re-run the viewport fill check when the list grows. */
  itemsCount?: number;
  /** Load more on mount / growth while the list does not overflow; false = user scroll only. */
  fillViewport?: boolean;
}

export function useInfiniteScrollLoadMore({
  scrollElement,
  hasMore,
  loading,
  onLoadMore,
  thresholdPx = 160,
  itemsCount,
  fillViewport = true,
}: UseInfiniteScrollLoadMoreOptions): void {
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const el = scrollElement;
    if (!el) return;

    const maybeLoad = () => {
      if (!hasMore || loading) return;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - thresholdPx) return;
      onLoadMoreRef.current();
    };

    if (fillViewport) maybeLoad();
    el.addEventListener("scroll", maybeLoad, { passive: true });
    return () => el.removeEventListener("scroll", maybeLoad);
  }, [scrollElement, hasMore, loading, thresholdPx, itemsCount, fillViewport]);
}
